import { useApplicationQuery, useCreateApplicationMutation, useUpdateApplicationMutation, useDeleteApplicationMutation, useProvisionApplicationMutation, useDeprovisionApplicationMutation, useMarkApplicationDecommissionedMutation } from './useApplicationsQuery';
import { Application } from '../types';

export const useApplication = (applicationId?: string) => {
    const { data: record, isLoading: loading, refetch: fetchRecord } = useApplicationQuery(applicationId || '');
    const createApplicationMutation = useCreateApplicationMutation();
    const updateApplicationMutation = useUpdateApplicationMutation();
    const deleteApplicationMutation = useDeleteApplicationMutation();
    const provisionApplicationMutation = useProvisionApplicationMutation();
    const deprovisionApplicationMutation = useDeprovisionApplicationMutation();
    const markDecommissionedMutation = useMarkApplicationDecommissionedMutation();

    const createRecord = async (values: Application) => {
        return await createApplicationMutation.mutateAsync(values);
    };

    const updateRecord = async (values: Application) => {
        if (typeof applicationId === 'undefined') {
            throw new Error('applicationId is required parameter');
        }
        return await updateApplicationMutation.mutateAsync({ id: applicationId, record: values });
    };

    const deleteRecord = async () => {
        if (typeof applicationId === 'undefined') {
            throw new Error('applicationId is required parameter');
        }
        return await deleteApplicationMutation.mutateAsync(applicationId);
    };

    // FNP-1013: BUILD the application's infrastructure, using the playbook the
    // admin chose. Creating the record and provisioning it are separate acts —
    // an application can exist as a record indefinitely without ever being
    // provisioned.
    //
    // ⚠️ `playbookId` is required and comes from the caller. It is not derived
    // from the application's name or type: a hardcoded id swapped for a
    // conventional one is still brittle, which is why the owner rejected
    // name-convention lookup.
    //
    // FNP-1499: `businessDomain` and `applicationType` used to be parameters
    // here. The playbook required them and nothing read them; both are gone.
    const provisionRecord = async (
        playbookId: string,
        stage: string,
        subdomain?: string,
    ) => {
        if (typeof applicationId === 'undefined') {
            throw new Error('applicationId is required parameter');
        }
        return await provisionApplicationMutation.mutateAsync({
            id: applicationId, playbookId, stage, subdomain,
        });
    };

    // FNP-998: tear down the application's INFRASTRUCTURE. Unlike deleteRecord
    // this keeps the record and marks it DECOMMISSIONED. `stage` is explicit
    // because a defaulted stage on a destructive operation is how the wrong
    // environment gets deleted.
    const deprovisionRecord = async (stage: string) => {
        if (typeof applicationId === 'undefined') {
            throw new Error('applicationId is required parameter');
        }
        return await deprovisionApplicationMutation.mutateAsync({ id: applicationId, stage });
    };

    // FNP-1007: correct the RECORD to match reality, destroying nothing.
    //
    // ⚠️ Not `deprovisionRecord`. That destroys infrastructure and takes 15-25
    // minutes; this asks the server to prove the infrastructure is already gone
    // and touches no AWS resource either way. Takes no `stage` — the sweep is
    // application-wide.
    //
    // Returns the structured result (including a REFUSAL, which resolves
    // normally rather than throwing) so the caller can render `found` and
    // `incompleteChecks` distinctly.
    const markRecordDecommissioned = async () => {
        if (typeof applicationId === 'undefined') {
            throw new Error('applicationId is required parameter');
        }
        return await markDecommissionedMutation.mutateAsync({ id: applicationId });
    };

    return {
        record,
        loading,
        fetchRecord,
        createRecord,
        updateRecord,
        deleteRecord,
        provisionRecord,
        provisioning: provisionApplicationMutation.isPending,
        deprovisionRecord,
        deprovisioning: deprovisionApplicationMutation.isPending,
        markRecordDecommissioned,
        markingDecommissioned: markDecommissionedMutation.isPending,
    };
};
